import { z } from 'zod'
import {
  ALL_OGR_EXTENSIONS,
  COMPANION_EXTENSIONS,
} from '@app/modules/Home/Data/constants/ogr.formats'
import {
  getExt,
  getBaseName,
  getMissingCompanions,
} from '@app/modules/Home/Data/utils/fileGroup.utils'

// A line does not have an area, etc
export const ALLOWED_METRIC_TYPES: Record<
  'line' | 'point' | 'polygon',
  Array<'count' | 'area' | 'length'>
> = {
  point: ['count'],
  line: ['count', 'length'],
  polygon: ['count', 'area'],
}

export const DataUploadSchema = z
  .object({
    name: z.string().min(1, 'Dataset name is required'),
    description: z.string().min(1, 'Description is required'),
    metricType: z.enum(['count', 'area', 'length'], { error: 'Metric type is required' }),
    geometryType: z.enum(['line', 'point', 'polygon'], { error: 'Geometry type is required' }),
    unit: z.string().min(1, 'Unit of measurement is required'),
    source: z.string().optional(),
    spatialExtent: z.string().optional(),
    icon: z.string().optional(),
    displaySource: z.string().optional(),
    type: z.string().optional(),
    utility: z.string().optional(),
    lineSegmentLength: z.number().optional(),
    polygonSegmentArea: z.number().optional(),
    changeToPoint: z.boolean().optional(),
    license: z.enum(['Restricted', 'Controlled', 'Open']).optional(),
    file: z.custom<FileList>((v) => v instanceof FileList && v.length > 0, 'File is required'),
  })
  .superRefine(({ geometryType, metricType }, ctx) => {
    if (!geometryType || !metricType) return
    if (!ALLOWED_METRIC_TYPES[geometryType].includes(metricType)) {
      ctx.addIssue({
        code: 'custom',
        message: `"${metricType}" is not valid for geometry type "${geometryType}"`,
        path: ['metricType'],
      })
    }
  })
  .superRefine(({ file }, ctx) => {
    if (!(file instanceof FileList) || file.length === 0) return

    const files = Array.from(file)
    const allowedSet = new Set(ALL_OGR_EXTENSIONS)

    const invalidFiles = files.filter((f) => !allowedSet.has(getExt(f)))
    if (invalidFiles.length > 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['file'],
        message: `Unsupported file type(s): ${invalidFiles.map((f) => f.name).join(', ')}. Only OGR vector formats are accepted.`,
      })
      return
    }

    const primaries = files.filter((f) => !COMPANION_EXTENSIONS.has(getExt(f)))
    if (primaries.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['file'],
        message:
          'No primary file found. Select a .shp, .geojson, .gpkg, or other primary format file.',
      })
      return
    }
    if (primaries.length > 1) {
      ctx.addIssue({
        code: 'custom',
        path: ['file'],
        message: `Multiple primary files selected (${primaries.map((f) => f.name).join(', ')}). Please select files for one dataset at a time.`,
      })
      return
    }

    const primaryBase = getBaseName(primaries[0])
    const companions = files.filter((f) => COMPANION_EXTENSIONS.has(getExt(f)))
    const mismatched = companions.filter((f) => getBaseName(f) !== primaryBase)
    if (mismatched.length > 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['file'],
        message: `Companion file(s) do not match the primary basename "${primaries[0].name}": ${mismatched.map((f) => f.name).join(', ')}`,
      })
      return
    }

    const missing = getMissingCompanions(files)
    if (missing.length > 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['file'],
        message: `Shapefile is missing required companion file(s): .${missing.join(', .')}`,
      })
    }
  })

export type DataUploadFormData = z.infer<typeof DataUploadSchema>

export type FieldConfig = {
  name: keyof DataUploadFormData
  label: string
  textbox?: boolean
  numeric?: boolean
}
